import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Svg } from 'src/components/Svg/Svg';
import { useIsoProjection } from 'src/hooks/useIsoProjection';
import { useScene } from 'src/hooks/useScene';
import { getConnectorLabelPlacement } from '@/utils/connectorDirectionIcon';

interface Props {
  connector: ReturnType<typeof useScene>['connectors'][number];
}

export const ConnectorLabel = ({ connector }: Props) => {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  const { items } = useScene();
  const description = connector.description ?? '';
  const labelWidth = Math.min(190, Math.max(64, description.length * 8 + 22));
  const { css, pxSize } = useIsoProjection({ ...connector.path.rectangle });

  useEffect(() => {
    const updateTheme = () =>
      setIsDark(document.documentElement.classList.contains('dark'));
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => observer.disconnect();
  }, []);

  const labelPosition = useMemo(() => {
    const origin = connector.path.rectangle.from;
    const obstacleTiles = items.map((item) => ({
      x: item.tile.x - origin.x,
      y: item.tile.y - origin.y
    }));

    return getConnectorLabelPlacement(connector.path.tiles, {
      obstacleTiles,
      labelWidthPx: labelWidth
    });
  }, [connector.path.tiles, connector.path.rectangle.from, items, labelWidth]);

  if (!description || !labelPosition) return null;

  const offsetFromLine = -26;

  return (
    <Box style={{ ...css, pointerEvents: 'none' }}>
      <Svg
        style={{
          transform: 'scale(-1, 1)',
          pointerEvents: 'none',
          overflow: 'visible'
        }}
        viewboxSize={pxSize}
        aria-hidden="true"
      >
        <g
          transform={`translate(${labelPosition.x}, ${labelPosition.y}) rotate(${labelPosition.rotation}) scale(-1, 1)`}
        >
          <rect
            x={-labelWidth / 2}
            y={offsetFromLine - 12}
            width={labelWidth}
            height={24}
            rx={7}
            fill={`var(--flowi-export-connector-label-bg, ${isDark ? '#0f172a' : '#ffffff'})`}
            stroke={`var(--flowi-export-connector-label-border, ${isDark ? '#334155' : '#e2e8f0'})`}
            fillOpacity={0.94}
            strokeWidth={1}
            style={{
              pointerEvents: 'none',
              filter: 'drop-shadow(0 1px 3px rgba(15, 23, 42, 0.14))'
            }}
          />
          <text
            x={0}
            y={offsetFromLine}
            textAnchor="middle"
            dominantBaseline="central"
            fill={`var(--flowi-export-connector-label-text, ${isDark ? '#f8fafc' : '#111827'})`}
            fontSize="15"
            fontWeight="600"
            style={{ pointerEvents: 'none' }}
            textRendering="geometricPrecision"
          >
            {description}
          </text>
        </g>
      </Svg>
    </Box>
  );
};
